<script setup lang="ts">
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { quranApi } from '@/lib/quranApi'
import {
  CURATED_RECITER_LABELS,
  CURATED_RECITER_ORDER,
} from '@/data/curatedReciters'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

defineProps<{
  selectedReciter: string
}>()

const emit = defineEmits<{
  'update:model-value': [value: string]
}>()

const { data: editionsData, isLoading } = useQuery({
  queryKey: ['quran', 'audio-editions'],
  queryFn: async () => {
    try {
      const data = await quranApi.getAudioEditionsInfo()
      return data.data || data
    } catch (error) {
      console.error('Failed to fetch audio editions:', error)
      return []
    }
  },
  staleTime: 1000 * 60 * 60 * 24,
})

const apiById = computed(() => {
  if (!editionsData.value) return new Map<string, any>()
  const data = Array.isArray(editionsData.value) ? editionsData.value : (editionsData.value as any)?.data || []
  const map = new Map<string, any>()
  for (const r of data) {
    if (!r?.identifier) continue
    const hasExplicitAudioType = r.format === 'audio' && r.type === 'versebyverse'
    const isLegacy = Boolean(r.identifier) && !('format' in r) && !('type' in r)
    if (hasExplicitAudioType || isLegacy) {
      map.set(r.identifier, r)
    }
  }
  return map
})

/** One row per curated reciter, in fixed order; labels from our list; API row merged when present. */
const displayReciters = computed(() => {
  return CURATED_RECITER_ORDER.map((id) => {
    const row = apiById.value.get(id)
    const label = CURATED_RECITER_LABELS[id] || id
    if (row) {
      return {
        ...row,
        englishName: label,
        name: row.name || label,
      }
    }
    return {
      identifier: id,
      englishName: label,
      name: label,
      format: 'audio',
      type: 'versebyverse',
    }
  })
})

const handleUpdate = (value: unknown) => {
  if (typeof value === 'string') {
    emit('update:model-value', value)
  }
}
</script>

<template>
  <div class="space-y-2">
    <label class="text-sm font-medium text-sidebar-foreground">
      Reciter
    </label>
    <Select :model-value="selectedReciter" @update:model-value="handleUpdate">
      <SelectTrigger class="w-full bg-sidebar-accent border-sidebar-border text-sidebar-foreground">
        <SelectValue :placeholder="isLoading ? 'Loading reciters...' : 'Select reciter'" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectLabel>Reciters</SelectLabel>
          <SelectItem
            v-for="reciter in displayReciters"
            :key="reciter.identifier"
            :value="reciter.identifier"
          >
            {{ reciter.englishName || reciter.name }}
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  </div>
</template>
